import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { api, formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, Home, MessageCircle, Phone, ExternalLink, Loader2, BarChart3, Star, ShieldCheck, Clock, CheckCircle2, XCircle, FileText } from "lucide-react";

const STATUS_COLORS = {
  new: "bg-amber-100 text-amber-800",
  contacted: "bg-blue-100 text-blue-800",
  converted: "bg-emerald-100 text-emerald-800",
  closed: "bg-slate-200 text-slate-700",
};

function formatPrice(v) {
  if (v == null) return "-";
  const n = Number(v);
  if (n >= 1_00_00_000) return `₹${(n / 1_00_00_000).toFixed(2)} Cr`;
  if (n >= 1_00_000) return `₹${(n / 1_00_000).toFixed(2)} L`;
  return `₹${n.toLocaleString("en-IN")}`;
}

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const [overview, setOverview] = useState(null);
  const [properties, setProperties] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [kycUsers, setKycUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [expiring, setExpiring] = useState(false);

  const reloadEnquiries = (status = statusFilter) => {
    const params = status && status !== "all" ? { status } : {};
    api.get("/admin/enquiries", { params }).then(({ data }) => setEnquiries(data)).catch(() => setEnquiries([]));
  };

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    setLoading(true);
    Promise.all([
      api.get("/admin/overview").then(({ data }) => setOverview(data)),
      api.get("/admin/properties").then(({ data }) => setProperties(data)),
      api.get("/admin/enquiries").then(({ data }) => setEnquiries(data)),
      api.get("/admin/kyc").then(({ data }) => setKycUsers(data)).catch(() => setKycUsers([])),
    ]).catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [user]);

  if (authLoading) return <div className="py-20 text-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/admin" }} replace />;
  if (user.role !== "admin") {
    return (
      <div className="max-w-md mx-auto py-20 text-center">
        <h2 className="text-2xl font-bold">403 — Admin only</h2>
        <p className="text-sm text-muted-foreground mt-2">Aap admin nahi hain.</p>
      </div>
    );
  }

  const updateStatus = async (eid, status) => {
    try {
      await api.patch(`/admin/enquiries/${eid}`, { status });
      toast.success("Status updated");
      setEnquiries((es) => es.map((e) => e.id === eid ? { ...e, status } : e));
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  const toggleFeature = async (p) => {
    try {
      const next = !p.featured;
      const { data } = await api.patch(`/admin/properties/${p.id}/feature`, { featured: next, days: 30 });
      setProperties((ps) => ps.map((x) => x.id === p.id ? { ...x, featured: data.featured, featured_until: data.featured_until } : x));
      toast.success(next ? "Featured for 30 days" : "Featured removed");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  const reviewKyc = async (uid, status, reason = "") => {
    try {
      await api.patch(`/admin/kyc/${uid}`, { status, reason: reason || null });
      setKycUsers((us) => us.map((u) => u.user_id === uid ? { ...u, kyc_status: status, kyc_reason: reason || null } : u));
      toast.success(`KYC ${status}`);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  const runExpirySweep = async () => {
    if (!window.confirm("Run expiry sweep? This will mark old (>60 days) active listings as inactive.")) return;
    setExpiring(true);
    try {
      const { data } = await api.post("/admin/properties/expire-old");
      toast.success(`Expired ${data.expired} listings`);
      const { data: props } = await api.get("/admin/properties");
      setProperties(props);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setExpiring(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <div className="overline text-secondary">Admin</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">BhoomiMitra Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage all properties, enquiries and buyer contacts.</p>
      </div>

      {loading ? (
        <div className="text-center py-16"><Loader2 className="animate-spin mx-auto text-muted-foreground" /></div>
      ) : (
        <>
          {/* OVERVIEW STATS */}
          {overview && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="admin-overview">
              <StatCard icon={<Home size={16} />} label="Active Properties" value={overview.properties.active} sub={`${overview.properties.total} total`} />
              <StatCard icon={<Users size={16} />} label="Users" value={overview.users.total} />
              <StatCard icon={<MessageCircle size={16} />} label="Enquiries" value={overview.enquiries.total} sub={`${overview.enquiries.new} new`} />
              <StatCard icon={<BarChart3 size={16} />} label="Conversion" value={overview.enquiries.total > 0 ? `${Math.round((overview.enquiries.total - overview.enquiries.new) / overview.enquiries.total * 100)}%` : "-"} />
            </div>
          )}

          <Tabs defaultValue="enquiries" className="w-full">
            <TabsList data-testid="admin-tabs">
              <TabsTrigger value="enquiries" data-testid="tab-enquiries">Enquiries ({enquiries.length})</TabsTrigger>
              <TabsTrigger value="properties" data-testid="tab-properties">Properties ({properties.length})</TabsTrigger>
              <TabsTrigger value="kyc" data-testid="tab-kyc">KYC ({kycUsers.filter((u) => u.kyc_status === "pending").length})</TabsTrigger>
            </TabsList>

            {/* ENQUIRIES TAB */}
            <TabsContent value="enquiries" className="mt-4">
              <Card className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-semibold">All Enquiries</div>
                  <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); reloadEnquiries(v); }}>
                    <SelectTrigger className="w-40" data-testid="enquiry-status-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="converted">Converted</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {enquiries.length === 0 ? (
                  <div className="text-center py-10 text-muted-foreground text-sm" data-testid="admin-empty-enquiries">Koi enquiry nahi hai.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                          <th className="py-2 pr-2">When</th>
                          <th className="py-2 pr-2">Buyer</th>
                          <th className="py-2 pr-2">Property</th>
                          <th className="py-2 pr-2">Notes</th>
                          <th className="py-2 pr-2">Status</th>
                          <th className="py-2 pr-2">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {enquiries.map((e) => (
                          <tr key={e.id} className="border-b border-border/60 hover:bg-accent/40" data-testid={`enq-row-${e.id}`}>
                            <td className="py-3 pr-2 text-xs text-muted-foreground whitespace-nowrap">
                              {new Date(e.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                            </td>
                            <td className="py-3 pr-2">
                              <div className="font-medium">{e.buyer_name}</div>
                              <a href={`tel:${e.buyer_phone}`} className="text-primary text-xs hover:underline flex items-center gap-1">
                                <Phone size={10} />{e.buyer_phone}
                              </a>
                            </td>
                            <td className="py-3 pr-2">
                              <Link to={`/property/${e.property_id}`} className="hover:text-primary flex items-center gap-1 text-xs">
                                {e.property_title} <ExternalLink size={10} />
                              </Link>
                              <div className="text-[10px] text-muted-foreground">{e.property_city} • {formatPrice(e.property_price)}</div>
                            </td>
                            <td className="py-3 pr-2 text-xs max-w-xs">
                              {e.preferred_time && <div className="text-muted-foreground">🕒 {e.preferred_time}</div>}
                              {e.message && <div className="italic">"{e.message}"</div>}
                            </td>
                            <td className="py-3 pr-2">
                              <Badge className={`${STATUS_COLORS[e.status] || ""} text-[10px] uppercase`}>{e.status}</Badge>
                            </td>
                            <td className="py-3 pr-2">
                              <Select value={e.status} onValueChange={(v) => updateStatus(e.id, v)}>
                                <SelectTrigger className="h-8 w-32 text-xs" data-testid={`enq-status-${e.id}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="new">New</SelectItem>
                                  <SelectItem value="contacted">Contacted</SelectItem>
                                  <SelectItem value="converted">Converted</SelectItem>
                                  <SelectItem value="closed">Closed</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </TabsContent>

            {/* PROPERTIES TAB */}
            <TabsContent value="properties" className="mt-4">
              <Card className="p-4">
                <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                  <div className="text-sm font-semibold">All Properties (with full contact info)</div>
                  <Button variant="outline" size="sm" onClick={runExpirySweep} disabled={expiring} data-testid="expire-sweep-btn">
                    {expiring ? <Loader2 className="animate-spin mr-1" size={12} /> : <Clock size={12} className="mr-1" />}
                    Sweep expired listings
                  </Button>
                </div>
                {properties.length === 0 ? (
                  <div className="text-center py-10 text-muted-foreground text-sm">Koi property nahi.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                          <th className="py-2 pr-2">Property</th>
                          <th className="py-2 pr-2">Category</th>
                          <th className="py-2 pr-2">City</th>
                          <th className="py-2 pr-2">Price</th>
                          <th className="py-2 pr-2">Owner</th>
                          <th className="py-2 pr-2">Contact</th>
                          <th className="py-2 pr-2">Enq.</th>
                          <th className="py-2 pr-2">Views</th>
                          <th className="py-2 pr-2">Featured</th>
                        </tr>
                      </thead>
                      <tbody>
                        {properties.map((p) => (
                          <tr key={p.id} className="border-b border-border/60 hover:bg-accent/40" data-testid={`prop-row-${p.id}`}>
                            <td className="py-3 pr-2 max-w-xs">
                              <Link to={`/property/${p.id}`} className="font-medium hover:text-primary line-clamp-1">{p.title}</Link>
                              <div className="text-[10px] text-muted-foreground">{p.id}</div>
                            </td>
                            <td className="py-3 pr-2"><Badge variant="outline" className="text-[10px] uppercase">{p.category}</Badge></td>
                            <td className="py-3 pr-2 text-xs">{p.city}, {p.state}</td>
                            <td className="py-3 pr-2 text-xs whitespace-nowrap">{formatPrice(p.price_value)} <span className="text-muted-foreground">/{p.size_value}{p.size_unit}</span></td>
                            <td className="py-3 pr-2 text-xs">
                              <div className="flex items-center gap-1">
                                {p.owner_name}
                                {p.owner_kyc_status === "verified" && <BadgeCheckMini />}
                              </div>
                            </td>
                            <td className="py-3 pr-2 text-xs">
                              {p.owner_phone ? (
                                <a href={`tel:${p.owner_phone}`} className="text-primary hover:underline flex items-center gap-1">
                                  <Phone size={10} />{p.owner_phone}
                                </a>
                              ) : <span className="text-muted-foreground">-</span>}
                              {p.owner_email && <div className="text-[10px] text-muted-foreground">{p.owner_email}</div>}
                            </td>
                            <td className="py-3 pr-2 text-center font-semibold">{p.enquiry_count || 0}</td>
                            <td className="py-3 pr-2 text-center text-xs">{p.views || 0}</td>
                            <td className="py-3 pr-2">
                              <Button
                                variant={p.featured ? "default" : "outline"}
                                size="sm"
                                className={`h-7 text-[10px] ${p.featured ? "bg-amber-500 hover:bg-amber-600 text-slate-900" : ""}`}
                                onClick={() => toggleFeature(p)}
                                data-testid={`feature-toggle-${p.id}`}
                              >
                                <Star size={11} className={p.featured ? "fill-current mr-1" : "mr-1"} />
                                {p.featured ? "Featured" : "Boost"}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </TabsContent>

            {/* KYC TAB */}
            <TabsContent value="kyc" className="mt-4">
              <Card className="p-4">
                <div className="text-sm font-semibold mb-3">KYC Submissions</div>
                {kycUsers.length === 0 ? (
                  <div className="text-center py-10 text-muted-foreground text-sm" data-testid="admin-empty-kyc">Koi KYC submission nahi.</div>
                ) : (
                  <div className="space-y-3">
                    {kycUsers.map((u) => (
                      <div key={u.user_id} className="border border-border rounded-lg p-3 flex flex-wrap items-start justify-between gap-3" data-testid={`kyc-row-${u.user_id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">{u.name}</span>
                            <Badge className={`text-[10px] uppercase ${
                              u.kyc_status === "verified" ? "bg-emerald-100 text-emerald-800" :
                              u.kyc_status === "rejected" ? "bg-red-100 text-red-800" :
                              "bg-amber-100 text-amber-800"
                            }`}>{u.kyc_status}</Badge>
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">{u.email} {u.phone && `• ${u.phone}`}</div>
                          {u.kyc_submitted_at && (
                            <div className="text-[10px] text-muted-foreground mt-0.5">
                              Submitted {new Date(u.kyc_submitted_at).toLocaleString("en-IN")}
                            </div>
                          )}
                          {u.kyc_note && <p className="text-xs italic mt-1">&ldquo;{u.kyc_note}&rdquo;</p>}
                          <div className="flex gap-2 mt-2 flex-wrap">
                            {u.kyc_aadhaar_path && (
                              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${u.kyc_aadhaar_path}`} target="_blank" rel="noreferrer" className="text-[11px] text-primary hover:underline inline-flex items-center gap-1">
                                <FileText size={10} /> Aadhaar
                              </a>
                            )}
                            {u.kyc_pan_path && (
                              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${u.kyc_pan_path}`} target="_blank" rel="noreferrer" className="text-[11px] text-primary hover:underline inline-flex items-center gap-1">
                                <FileText size={10} /> PAN
                              </a>
                            )}
                          </div>
                          {u.kyc_reason && (
                            <div className="mt-1 text-[11px] text-destructive">Reason: {u.kyc_reason}</div>
                          )}
                        </div>
                        {u.kyc_status === "pending" && (
                          <div className="flex gap-2 shrink-0">
                            <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => reviewKyc(u.user_id, "verified")} data-testid={`kyc-verify-${u.user_id}`}>
                              <CheckCircle2 size={12} className="mr-1" />Verify
                            </Button>
                            <Button size="sm" variant="outline" className="h-8 border-destructive/50 text-destructive" onClick={() => {
                              const reason = window.prompt("Rejection reason (optional)?");
                              reviewKyc(u.user_id, "rejected", reason || "");
                            }} data-testid={`kyc-reject-${u.user_id}`}>
                              <XCircle size={12} className="mr-1" />Reject
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, sub }) {
  return (
    <Card className="p-4">
      <div className="text-muted-foreground flex items-center gap-1.5 text-xs">{icon}{label}</div>
      <div className="text-3xl font-bold text-primary tracking-tight mt-1">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </Card>
  );
}

function BadgeCheckMini() {
  return (
    <span title="KYC Verified" className="inline-flex items-center text-emerald-600">
      <ShieldCheck size={12} />
    </span>
  );
}
